import { useMemo, useCallback, useState } from "react";
import { UserData, UserStatus } from "@/entities/users/types";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import UserManagementTable from "@/pages/settings/components/userManagement/UserManagementTable";
import DeactivateUserModal from "@/pages/settings/components/DeactivateUserModal";
import BulkActionPopover from "@/pages/settings/components/userManagement/BulkActionPopover";
import ChangeReportingManagerModal from "@/pages/settings/components/userManagement/ChangeReportingManagerModal";
import ChangeRoleModal from "@/pages/settings/components/userManagement/ChangeRoleModal";
import BulkDeactivateUsersModal from "@/pages/settings/components/userManagement/BulkDeactivateUsersModal";
import AddNewUserModal from "@/pages/settings/components/userManagement/AddNewUserModal";
import EditUserDrawer from "@/pages/settings/components/userManagement/EditUserDrawer";
import Text from "@/components/ui/text";
import { useUserManagement } from "@/pages/settings/components/userManagement/hooks/useUserManagement";
import {
  useBulkUpdateUsers,
  useResetUsersInfinite,
  useRoles,
  useUserProvisioning
} from "@/entities/users/hooks";
import { Add } from "@dreamhub/designsystem/icons";
import IconContainer from "@/components/ui/icon-container";
import { useUndoToast } from "@/hooks/useUndoToast";
import {
  BulkActionConfig,
  TransferData,
  TRANSLATION_KEY,
  UndoData,
  UndoUserData
} from "@/pages/settings/components/userManagement/hooks/types";
import { useUserDeactivation } from "@/pages/settings/components/userManagement/hooks/useUserDeactivation";
import {
  getTransferErrorTranslationKey,
  isManagedByIdentityProvider,
  selectUsersToDeactivate
} from "@/pages/settings/components/userManagement/helpers";

export const UserManagementSettings = () => {
  const { t } = useTranslation();
  const bulkUpdateUsers = useBulkUpdateUsers();
  const { deactivateUsers } = useUserDeactivation();
  const resetUsersInfinite = useResetUsersInfinite();
  const { showUndoToast } = useUndoToast<UndoData>();
  const { data: rolesData } = useRoles();
  const { data: provisioning } = useUserProvisioning();

  const {
    searchTerm,
    activeFilter,
    selectedUsers,
    selectAll,
    users,
    selectedUserData,
    filterTabs,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    isError,
    sorting,
    setSearchTerm,
    setActiveFilter,
    handleUserSelect,
    handleSelectAll,
    clearSelection,
    onLoadMore,
    onSortingChange
  } = useUserManagement();

  const [deactivateModalOpen, setDeactivateModalOpen] = useState(false);
  const [userToDeactivate, setUserToDeactivate] = useState<UserData | null>(
    null
  );
  const [changeReportingManagerModalOpen, setChangeReportingManagerModalOpen] =
    useState(false);
  const [changeRoleModalOpen, setChangeRoleModalOpen] = useState(false);
  const [bulkDeactivateUsersModalOpen, setBulkDeactivateUsersModalOpen] =
    useState(false);
  const [addNewUserModalOpen, setAddNewUserModalOpen] = useState(false);
  const [editUserModalOpen, setEditUserModalOpen] = useState(false);
  const [userToEdit, setUserToEdit] = useState<UserData | null>(null);

  const handleDeactivateUser = useCallback((userData: UserData) => {
    setUserToDeactivate(userData);
    setDeactivateModalOpen(true);
  }, []);

  const handleEditUser = useCallback((userData: UserData) => {
    setUserToEdit(userData);
    setEditUserModalOpen(true);
  }, []);

  const handleActivateUser = useCallback(
    async (userData: UserData) => {
      try {
        await bulkUpdateUsers.mutateAsync([
          { id: userData.id, status: UserStatus.ACTIVE }
        ]);
        resetUsersInfinite();
      } catch (error) {
        toast.error("Failed to activate user");
      }
    },
    [t, bulkUpdateUsers, resetUsersInfinite]
  );

  const handleDeactivateConfirm = useCallback(
    async (transferData: TransferData) => {
      if (userToDeactivate) {
        try {
          await deactivateUsers([userToDeactivate.id], transferData);

          resetUsersInfinite();
          setUserToDeactivate(null);
          setDeactivateModalOpen(false);

          toast.success(
            t(`${TRANSLATION_KEY}.deactivateModal.successMessage`, {
              firstName: userToDeactivate.firstName
            }),
            { duration: 5000 }
          );
        } catch (error) {
          const transferErrorTranslationKey =
            getTransferErrorTranslationKey(error);
          toast.error(
            transferErrorTranslationKey
              ? t(transferErrorTranslationKey)
              : "Failed to deactivate user"
          );
        }
      }
    },
    [userToDeactivate, t, deactivateUsers, resetUsersInfinite]
  );

  const handleDeactivateCancel = useCallback(() => {
    setUserToDeactivate(null);
    setDeactivateModalOpen(false);
  }, []);

  const isUserManagedByIdentityProvider = useCallback(
    (userData: UserData) =>
      isManagedByIdentityProvider(userData, provisioning),
    [provisioning]
  );

  const selectionHasManagedUsers = useMemo(
    () => selectedUserData.some(isUserManagedByIdentityProvider),
    [selectedUserData, isUserManagedByIdentityProvider]
  );

  const bulkActionConfig: BulkActionConfig = useMemo(() => {
    if (selectedUserData.length === 0 || selectionHasManagedUsers) {
      return { showActivate: false, showDeactivate: false };
    }

    const activeUsers = selectedUserData.filter(
      (user) => user.status === UserStatus.ACTIVE
    );
    const inactiveUsers = selectedUserData.filter(
      (user) => user.status === UserStatus.INACTIVE
    );

    const hasActiveOnly = activeUsers.length > 0 && inactiveUsers.length === 0;
    const hasInactiveOnly =
      inactiveUsers.length > 0 && activeUsers.length === 0;
    const hasMixed = activeUsers.length > 0 && inactiveUsers.length > 0;

    return {
      showActivate: hasInactiveOnly || hasMixed,
      showDeactivate: hasActiveOnly || hasMixed
    };
  }, [selectedUserData, selectionHasManagedUsers]);

  const handleBulkReportsTo = useCallback(
    () => setChangeReportingManagerModalOpen(true),
    []
  );

  const handleBulkChangeRole = useCallback(
    () => setChangeRoleModalOpen(true),
    []
  );

  const handleBulkDeactivate = useCallback(
    () => setBulkDeactivateUsersModalOpen(true),
    []
  );

  const handleBulkActivate = useCallback(async () => {
    const userIds = selectedUserData.map((u) => u.id);

    try {
      await bulkUpdateUsers.mutateAsync(
        userIds.map((id) => ({ id, status: UserStatus.ACTIVE }))
      );
      resetUsersInfinite();
      clearSelection();
      toast.success(`Successfully activated ${selectedUserData.length} users`);
    } catch (error) {
      toast.error("Failed to activate users");
    }
  }, [selectedUserData, clearSelection, bulkUpdateUsers, resetUsersInfinite]);

  const handleCloseBulkActions = useCallback(
    () => clearSelection(),
    [clearSelection]
  );

  const handleChangeReportingManager = useCallback(
    async (newManagerId: string) => {
      const userIds = selectedUserData.map((u) => u.id);

      const previousValues: UndoUserData[] = selectedUserData.map((user) => ({
        id: user.id,
        previousValue: user.managerId || undefined
      }));

      try {
        await bulkUpdateUsers.mutateAsync(
          userIds.map((id) => ({ id, managerId: newManagerId }))
        );

        resetUsersInfinite();
        clearSelection();

        const undoAction = async (undoData: UndoData) => {
          await bulkUpdateUsers.mutateAsync(
            undoData.users.map((user) => ({
              id: user.id,
              managerId: user.previousValue || undefined
            }))
          );
          resetUsersInfinite();
          toast.success("Changes undone successfully");
        };

        showUndoToast(
          `The reporting manager successfully updated for ${selectedUserData.length} selected users.`,
          {
            users: previousValues,
            action: "reportingManager" as const
          },
          undoAction
        );
      } catch (error) {
        toast.error("Failed to update reporting manager");
      }
    },
    [
      selectedUserData,
      clearSelection,
      bulkUpdateUsers,
      resetUsersInfinite,
      showUndoToast
    ]
  );

  const handleChangeRole = useCallback(
    async (newRoleId: string) => {
      const userIds = selectedUserData.map((u) => u.id);

      // Get department ID from selected role
      const selectedRole = rolesData?.roles?.find(
        (role: any) => role.id === newRoleId
      );
      const departmentId = selectedRole?.department || null;

      const previousValues: UndoUserData[] = selectedUserData.map((user) => ({
        id: user.id,
        previousValue: user.role?.toString(),
        previousRole: user.role,
        previousDepartment: user.department
      }));

      try {
        await bulkUpdateUsers.mutateAsync(
          userIds.map((id) => ({
            id,
            role: newRoleId,
            department: departmentId
          }))
        );

        resetUsersInfinite();
        clearSelection();

        const undoAction = async (undoData: UndoData) => {
          await bulkUpdateUsers.mutateAsync(
            undoData.users.map((user) => ({
              id: user.id,
              role: user.previousRole,
              department: user.previousDepartment
            }))
          );
          resetUsersInfinite();
          toast.success("Changes undone successfully");
        };

        showUndoToast(
          `The role successfully updated for ${selectedUserData.length} selected users.`,
          {
            users: previousValues,
            action: "role" as const
          },
          undoAction
        );
      } catch (error) {
        toast.error("Failed to update user roles");
      }
    },
    [
      selectedUserData,
      clearSelection,
      bulkUpdateUsers,
      resetUsersInfinite,
      showUndoToast,
      rolesData
    ]
  );

  const handleBulkDeactivateUsersConfirm = useCallback(
    async (transferData: TransferData) => {
      const usersToDeactivate = selectUsersToDeactivate(selectedUserData);
      const userIds = usersToDeactivate.map((user) => user.id);

      try {
        await deactivateUsers(userIds, transferData);

        resetUsersInfinite();
        clearSelection();
        toast.success(
          `Successfully deactivated ${usersToDeactivate.length} users`
        );
      } catch (error) {
        const transferErrorTranslationKey =
          getTransferErrorTranslationKey(error);
        toast.error(
          transferErrorTranslationKey
            ? t(transferErrorTranslationKey)
            : "Failed to deactivate users"
        );
      }
    },
    [selectedUserData, clearSelection, deactivateUsers, resetUsersInfinite, t]
  );

  const handleAddUserModalClose = useCallback(
    (userWasAdded?: boolean) => {
      setAddNewUserModalOpen(false);
      if (userWasAdded) {
        resetUsersInfinite();
      }
    },
    [resetUsersInfinite]
  );

  const handleEditUserModalClose = useCallback(
    (userWasUpdated?: boolean) => {
      setEditUserModalOpen(false);
      setUserToEdit(null);
      if (userWasUpdated) {
        resetUsersInfinite();
      }
    },
    [resetUsersInfinite]
  );

  const handleOpenAddUserModal = useCallback(
    () => setAddNewUserModalOpen(true),
    []
  );

  const headerActions = (
    <Button
      className="flex gap-2"
      onClick={handleOpenAddUserModal}
      variant="primary"
      data-test="settings-user-management-add-button"
    >
      <IconContainer size="sm" color="on-primary">
        <Add />
      </IconContainer>
      {t(`${TRANSLATION_KEY}.addUser`)}
    </Button>
  );

  return (
    <div
      className="w-full h-screen flex flex-col bg-surface-elevation0 gap-6"
      data-test="settings-user-management-page"
    >
      <div
        className="flex items-center justify-between px-6 mt-12"
        data-test="settings-user-management-header"
      >
        <Text variant="title3H1">{t(`${TRANSLATION_KEY}.title`)}</Text>
        <div className="flex items-center gap-3">{headerActions}</div>
      </div>

      <UserManagementTable
        data-test="settings-user-management-table"
        users={users as UserData[]}
        filterTabs={filterTabs}
        isLoading={isLoading}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        isError={isError}
        searchTerm={searchTerm}
        activeFilter={activeFilter}
        selectedUsers={selectedUsers}
        selectAll={selectAll}
        sorting={sorting}
        onSearchChange={setSearchTerm}
        onFilterChange={setActiveFilter}
        onUserSelect={handleUserSelect}
        onSelectAll={handleSelectAll}
        onDeactivateUser={handleDeactivateUser}
        onActivateUser={handleActivateUser}
        onEditUser={handleEditUser}
        isUserManagedByIdentityProvider={isUserManagedByIdentityProvider}
        onLoadMore={onLoadMore}
        onSortingChange={onSortingChange}
        className="flex-1"
      />

      {selectedUsers.size > 0 && (
        <BulkActionPopover
          data-test="settings-user-management-bulk-actions"
          selectedUsers={selectedUserData}
          onReportsTo={handleBulkReportsTo}
          onChangeRole={handleBulkChangeRole}
          onActivate={handleBulkActivate}
          onDeactivate={handleBulkDeactivate}
          onClose={handleCloseBulkActions}
          showChangeRole={!selectionHasManagedUsers}
          showActivate={bulkActionConfig.showActivate}
          showDeactivate={bulkActionConfig.showDeactivate}
        />
      )}

      {userToDeactivate && (
        <DeactivateUserModal
          data-test="settings-user-management-deactivate-modal"
          isOpen={deactivateModalOpen}
          onClose={handleDeactivateCancel}
          userData={userToDeactivate}
          onDeactivate={handleDeactivateConfirm}
        />
      )}

      <ChangeReportingManagerModal
        data-test="settings-user-management-change-manager-modal"
        isOpen={changeReportingManagerModalOpen}
        onClose={() => setChangeReportingManagerModalOpen(false)}
        selectedUsers={selectedUserData}
        onApplyChanges={handleChangeReportingManager}
      />

      <ChangeRoleModal
        data-test="settings-user-management-change-role-modal"
        isOpen={changeRoleModalOpen}
        onClose={() => setChangeRoleModalOpen(false)}
        selectedUsers={selectedUserData}
        onApplyChanges={handleChangeRole}
      />

      <BulkDeactivateUsersModal
        data-test="settings-user-management-bulk-deactivate-modal"
        isOpen={bulkDeactivateUsersModalOpen}
        onClose={() => setBulkDeactivateUsersModalOpen(false)}
        selectedUsers={selectedUserData}
        onDeactivate={handleBulkDeactivateUsersConfirm}
      />

      <AddNewUserModal
        data-test="settings-user-management-add-modal"
        isOpen={addNewUserModalOpen}
        onClose={handleAddUserModalClose}
      />

      <EditUserDrawer
        data-test="settings-user-management-edit-drawer"
        isOpen={editUserModalOpen}
        onClose={handleEditUserModalClose}
        userData={userToEdit}
        isManagedByIdentityProvider={
          !!userToEdit && isUserManagedByIdentityProvider(userToEdit)
        }
      />
    </div>
  );
};
